// ------------------------------
// src/pages/BlogsPage.js
// PREMIUM MAGAZINE VERSION
// ------------------------------

import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";

import { Helmet } from "react-helmet-async";

import { db } from "../firebaseConfig";

import Header from "../components/Header";
import EarnWithUsModal from "../components/EarnWithUsModal";

import "./blog.css";

export default function BlogsPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [searchParams, setSearchParams] =
    useSearchParams();

  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  const [isMobileMenuOpen, setIsMobileMenuOpen] =
    useState(false);

  const [scrolled, setScrolled] = useState(false);

  const [showEarnModal, setShowEarnModal] =
    useState(false);

  const theme = "light";

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const q = (searchParams.get("q") || "").trim();

  const tag = (searchParams.get("tag") || "").trim();

  const pageTitle = useMemo(() => {
    const base = "Fashion Rental Blog | DOR Dress On Rent";
    if (q) return `Search: ${q} | Fashion Rental Blog | DOR Dress On Rent`;
    if (tag) return `#${tag} Style Stories | Fashion Rental Blog | DOR Dress On Rent`;
    return base;
  }, [q, tag]);

  const pageDesc =
    "Explore wedding style guides, designer outfit rental tips, lehenga & sherwani styling, and occasion wear advice from DOR Dress On Rent in Pune & Nagpur.";

  // FETCH POSTS
  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);

      setError("");

      try {
        const preload = query(
          collection(db, "blogs"),
          where("status", "==", "published"),
          orderBy("createdAt", "desc"),
          limit(50)
        );

        const baseQuery = query(
          collection(db, "blogs"),
          where("status", "==", "published"),
          orderBy("createdAt", "desc"),
          limit(10)
        );

        const snap = await getDocs(
          q || tag ? preload : baseQuery
        );

        let rows = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        // SEARCH
        if (q) {
          const needle = q.toLowerCase();

          rows = rows.filter(
            (p) =>
              (p.title || "")
                .toLowerCase()
                .includes(needle) ||
              (p.excerpt || "")
                .toLowerCase()
                .includes(needle) ||
              (p.content || "")
                .toLowerCase()
                .includes(needle)
          );
        }

        // TAG
        if (tag) {
          rows = rows.filter(
            (p) =>
              Array.isArray(p.tags) &&
              p.tags
                .map((t) => t.toLowerCase())
                .includes(tag.toLowerCase())
          );
        }

        setPosts(rows);

        setLastDoc(
          snap.docs[snap.docs.length - 1] || null
        );

        setHasMore(
          snap.docs.length === 10 && !q && !tag
        );
      } catch (e) {
        console.error(e);

        setError(
          "Couldn't load blog posts. Please try again later."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [q, tag]);

  // LOAD MORE
  const loadMore = async () => {
    if (!lastDoc) return;

    try {
      const nextQ = query(
        collection(db, "blogs"),
        where("status", "==", "published"),
        orderBy("createdAt", "desc"),
        startAfter(lastDoc),
        limit(10)
      );

      const snap = await getDocs(nextQ);

      setPosts((prev) => [
        ...prev,
        ...snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        })),
      ]);

      setLastDoc(
        snap.docs[snap.docs.length - 1] || null
      );

      setHasMore(snap.docs.length === 10);
    } catch (e) {
      console.error(e);
    }
  };

  // FEATURED POST
  const featuredPost = posts[0];

  const gridPosts = posts.slice(1);

  // READING TIME
  const getReadingTime = (content = "") => {
    const words = content
      .replace(/<[^>]+>/g, "")
      .split(" ").length;

    return `${Math.max(
      1,
      Math.ceil(words / 200)
    )} min read`;
  };

  return (
    <section className="home-page blox-pad light-theme">
      <Helmet>
        <title>{pageTitle}</title>

        <meta
          name="description"
          content={pageDesc}
        />

        <meta
          name="robots"
          content={q || tag ? "noindex, follow" : "index, follow"}
        />

        <link rel="canonical" href="https://www.dordressonrent.com/blog" />

        {/* Open Graph Meta Tags */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content="https://www.dordressonrent.com/blog" />
        <meta property="og:site_name" content="DOR Dress On Rent" />
        <meta
          property="og:image"
          content={featuredPost?.coverImage || "https://www.dordressonrent.com/og-image.jpg"}
        />

        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        <meta
          name="twitter:image"
          content={featuredPost?.coverImage || "https://www.dordressonrent.com/og-image.jpg"}
        />
      </Helmet>

      <EarnWithUsModal
        showEarnModal={showEarnModal}
        setShowEarnModal={setShowEarnModal}
        theme={theme}
      />

      <Header
        scrolled={scrolled}
        isMobileMenuOpen={isMobileMenuOpen}
        toggleMobileMenu={toggleMobileMenu}
        setShowEarnModal={setShowEarnModal}
      />

      <div className="blox-shell">

        {/* HERO */}

        <header className="blox-hero">
          <div className="blox-heroCard">

            <div className="blox-heroTop">

              <div>
                <h1 className="blox-title">
                  DOR Blog
                </h1>

                <p className="blox-heroSub">
                  Fashion inspiration, rental tips, and style guides for designer outfits on rent in Pune and Nagpur.
                </p>
              </div>

              <form
                className="blox-search"
                role="search"
                onSubmit={(e) => {
                  e.preventDefault();

                  const input = new FormData(
                    e.currentTarget
                  ).get("q");

                  const next = {};

                  if (input) next.q = input;

                  if (tag) next.tag = tag;

                  setSearchParams(next);
                }}
              >
                <input
                  name="q"
                  defaultValue={q}
                  placeholder="Search articles..."
                  className="blox-input"
                />

                <button className="blox-btn">
                  Search
                </button>
              </form>
            </div>
          </div>
        </header>

        {/* FEATURED */}

        {!loading && featuredPost && (
          <Link
            to={`/blog/${
              featuredPost.slug || featuredPost.id
            }`}
            className="blox-featured"
          >
            <div className="blox-featuredImageWrap">

              <img
                src={
                  featuredPost.coverImage ||
                  "/placeholder.jpg"
                }
                alt={`${featuredPost.title} - DOR Dress On Rent`}
                className="blox-featuredImage"
              />

              <div className="blox-featuredOverlay" />

            </div>

            <div className="blox-featuredContent">

              <div className="blox-featuredMeta">
                {getReadingTime(
                  featuredPost.content
                )}
              </div>

              <h2 className="blox-featuredTitle">
                {featuredPost.title}
              </h2>

              <p className="blox-featuredExcerpt">
                {featuredPost.excerpt}
              </p>

              <span className="blox-featuredCTA">
                Read Article →
              </span>
            </div>
          </Link>
        )}

        {/* LOADING */}

        {loading && (
          <ul className="blox-grid">
            {Array.from({ length: 6 }).map((_, i) => (
              <li key={i} className="blox-skel">
                <div className="blox-skelImg" />
                <div className="blox-skelLine blox-w70" />
                <div className="blox-skelLine blox-w40" />
              </li>
            ))}
          </ul>
        )}

        {/* ERROR */}

        {error && (
          <p
            className="blox-text"
            style={{ color: "#ef4444" }}
          >
            {error}
          </p>
        )}

        {/* GRID */}

        {!loading && (
          <>
            <ul className="blox-grid">
              {gridPosts.map((p) => (
                <li key={p.id} className="blox-card">

                  <Link
                    to={`/blog/${p.slug || p.id}`}
                    className="blox-cardLink"
                  >
                    <div className="blox-cardImageWrap">

                      <img
                        className="blox-cardImg"
                        src={
                          p.coverImage ||
                          "/placeholder.jpg"
                        }
                        alt={`${p.title} - DOR Dress On Rent`}
                        loading="lazy"
                      />

                    </div>

                    <div className="blox-cardBody">

                      <div className="blox-cardMeta">
                        {getReadingTime(p.content)}
                      </div>

                      <h3 className="blox-cardTitle">
                        {p.title}
                      </h3>

                      <p className="blox-cardSub">
                        {p.excerpt || "Read article"}
                      </p>

                      {Array.isArray(p.tags) &&
                        p.tags.length > 0 && (
                          <div className="blox-tags">
                            {p.tags
                              .slice(0, 3)
                              .map((t) => (
                                <button
                                  key={t}
                                  className="blox-chip"
                                  onClick={(e) => {
                                    e.preventDefault();

                                    setSearchParams({
                                      tag: t,
                                    });
                                  }}
                                >
                                  #{t}
                                </button>
                              ))}
                          </div>
                        )}

                      <div className="blox-cardFooter">

                        <span className="blox-readMore">
                          Read Article →
                        </span>

                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>

            {/* EMPTY */}

            {posts.length === 0 && (
              <div className="blox-empty">
                <p>
                  No posts found. Try another search.
                </p>
              </div>
            )}

            {/* LOAD MORE */}

            {hasMore && (
              <div className="blox-loadWrap">
                <button
                  className="blox-btn"
                  onClick={loadMore}
                >
                  Load More Articles
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}